import { useCallback, useEffect, useState } from 'react';
import { Giveaway, GiveawayRequirements, User } from '../types';
import { giveaways as giveawaysApi } from '../api';
import SlabCard from '../components/SlabCard';
import RarityBadge from '../components/RarityBadge';

interface GiveawaysProps {
  user: User;
  deepLinkGiveawayId?: number;
  onDeepLinkHandled?: () => void;
}

function timeRemaining(endsAt: string): string {
  const diff = new Date(endsAt).getTime() - Date.now();
  if (diff <= 0) return 'Ended';
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function parseReqs(reqs: string): GiveawayRequirements {
  try { return JSON.parse(reqs); } catch { return {}; }
}

interface RequirementsListProps {
  reqs: GiveawayRequirements;
  channelUsername: string | null;
  isPremium?: boolean;
  checks?: Record<string, boolean>;
  onRecheck?: () => void;
  checking?: boolean;
}

function RequirementsList({ reqs, channelUsername, isPremium, checks = {}, onRecheck, checking }: RequirementsListProps) {
  const items: { label: string; met: boolean; url: string | null }[] = [];

  if (reqs.follow_slabvault) {
    items.push({ label: 'Follow @slabvault', met: checks.follow_slabvault ?? false, url: 'https://t.me/slabvault' });
  }
  if (reqs.follow_channel && channelUsername) {
    const ch = channelUsername.replace('@', '');
    items.push({ label: `Follow @${ch}`, met: checks.follow_channel ?? false, url: `https://t.me/${ch}` });
  }
  if (reqs.premium_only) {
    items.push({ label: 'Telegram Premium required', met: Boolean(isPremium), url: null });
  }
  if (reqs.opened_pack) {
    items.push({ label: 'Must have opened a pack', met: checks.opened_pack ?? false, url: null });
  }
  if (reqs.has_traded) {
    items.push({ label: 'Must have traded on SlabVault', met: checks.has_traded ?? false, url: null });
  }

  if (items.length === 0) return null;

  return (
    <div className="space-y-1.5 mt-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide">Requirements</p>
        {onRecheck && (
          <button
            onClick={onRecheck}
            disabled={checking}
            className="text-accent-purple-light text-xs flex items-center gap-1 disabled:opacity-50"
          >
            {checking ? '⏳' : '🔄'} Recheck
          </button>
        )}
      </div>
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 text-sm">
          <span className={item.met ? 'text-green-400' : 'text-gray-500'}>
            {item.met ? '✅' : '⬜'}
          </span>
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline underline-offset-2 ${item.met ? 'text-green-300' : 'text-accent-purple-light'}`}
            >
              {item.label}
            </a>
          ) : (
            <span className={item.met ? 'text-green-300' : 'text-gray-400'}>{item.label}</span>
          )}
        </div>
      ))}
    </div>
  );
}

interface DetailModalProps {
  giveaway: Giveaway;
  user: User;
  onClose: () => void;
  onEntered: () => void;
}

function DetailModal({ giveaway, user, onClose, onEntered }: DetailModalProps) {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entered, setEntered] = useState(Boolean(giveaway.user_entered));
  const [checks, setChecks] = useState<Record<string, boolean>>({});

  const tgUser = (window as Window & { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number; first_name?: string; is_premium?: boolean } } } } }).Telegram?.WebApp?.initDataUnsafe?.user;
  const tgId = String(tgUser?.id ?? user.tg_id ?? '0');
  const tgName = tgUser?.first_name ?? user.tg_name ?? 'User';
  const isPremium = Boolean(tgUser?.is_premium);

  const reqs = parseReqs(giveaway.requirements);
  const isActive = giveaway.status === 'active' && new Date(giveaway.ends_at) > new Date();

  async function handleCheck() {
    setChecking(true);
    try {
      const res = await fetch(`/api/giveaways/${giveaway.id}/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tg_id: tgId, tg_name: tgName, is_premium: isPremium }),
      });
      const data = await res.json() as { checks: Record<string, boolean> };
      setChecks(data.checks ?? {});
    } catch { /* ignore */ }
    finally { setChecking(false); }
  }

  // Auto-check on open
  useState(() => { handleCheck(); });

  async function handleEnter() {
    setLoading(true);
    setError(null);
    try {
      await giveawaysApi.enter(giveaway.id, tgId, tgName);
      setEntered(true);
      onEntered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enter giveaway');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div
        className="w-full bg-surface-card rounded-t-2xl p-4 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />

        <div className="w-36 mx-auto">
          <SlabCard card={giveaway} size="md" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-white font-bold text-lg">{giveaway.pokemon_name}</h3>
          <div className="flex items-center justify-center gap-2">
            <RarityBadge rarity={giveaway.rarity} />
            <span className="text-accent-gold font-semibold">${giveaway.market_price_usd.toFixed(2)}</span>
          </div>
          <p className="text-gray-400 text-sm">
            By <span className="text-accent-purple-light">
              {giveaway.owner_username ? `@${giveaway.owner_username}` : giveaway.owner_name}
            </span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-surface rounded-xl p-3 text-center">
            <p className="text-white font-bold text-lg">{giveaway.entry_count ?? 0}</p>
            <p className="text-gray-500 text-xs">entries</p>
          </div>
          <div className="bg-surface rounded-xl p-3 text-center">
            <p className={`font-bold text-lg ${isActive ? 'text-green-400' : 'text-gray-500'}`}>
              {timeRemaining(giveaway.ends_at)}
            </p>
            <p className="text-gray-500 text-xs">remaining</p>
          </div>
        </div>

        <RequirementsList
          reqs={reqs}
          channelUsername={giveaway.channel_username}
          isPremium={isPremium}
          checks={checks}
          onRecheck={handleCheck}
          checking={checking}
        />

        {error && <p className="text-red-400 text-sm text-center">{error}</p>}

        {isActive && !entered && (
          <button
            onClick={handleEnter}
            disabled={loading}
            className="w-full py-3 rounded-xl bg-accent-purple text-white font-bold disabled:opacity-50"
          >
            {loading ? 'Entering…' : '🎁 Enter Now'}
          </button>
        )}

        {entered && (
          <div className="w-full py-3 rounded-xl bg-green-900/40 text-green-400 font-semibold text-center border border-green-500/30">
            You're entered! 🎉
          </div>
        )}

        {!isActive && !entered && (
          <div className="w-full py-3 rounded-xl bg-surface text-gray-500 font-semibold text-center">
            Giveaway ended
          </div>
        )}

        <button onClick={onClose} className="w-full py-2 text-gray-500 text-sm">Close</button>
      </div>
    </div>
  );
}

function GiveawayCard({ giveaway, onClick }: { giveaway: Giveaway; onClick: () => void }) {
  const reqs = parseReqs(giveaway.requirements);
  const reqCount = Object.values(reqs).filter(Boolean).length;
  const isActive = giveaway.status === 'active' && new Date(giveaway.ends_at) > new Date();

  return (
    <div
      className="bg-surface-card rounded-2xl overflow-hidden border border-white/5 active:scale-[0.98] transition-transform"
      onClick={onClick}
    >
      <div className="flex gap-3 p-3">
        <div className="w-20 flex-shrink-0">
          <SlabCard card={giveaway} size="sm" />
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-start justify-between gap-1">
            <p className="text-white font-bold text-sm leading-tight">{giveaway.pokemon_name}</p>
            <span className={`text-xs font-semibold flex-shrink-0 ${isActive ? 'text-green-400' : 'text-gray-500'}`}>
              {timeRemaining(giveaway.ends_at)}
            </span>
          </div>
          <RarityBadge rarity={giveaway.rarity} />
          <p className="text-accent-gold text-xs font-semibold">${giveaway.market_price_usd.toFixed(2)}</p>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{giveaway.entry_count ?? 0} entries</span>
            {reqCount > 0 && <span>{reqCount} req{reqCount > 1 ? 's' : ''}</span>}
          </div>
          {giveaway.user_entered && (
            <span className="inline-block text-[10px] font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
              Entered ✓
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Giveaways({ user, deepLinkGiveawayId, onDeepLinkHandled }: GiveawaysProps) {
  const [giveaways, setGiveaways] = useState<Giveaway[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Giveaway | null>(null);

  const tgUser = (window as Window & { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number } } } } }).Telegram?.WebApp?.initDataUnsafe?.user;
  const tgId = String(tgUser?.id ?? user.tg_id ?? '0');

  const loadGiveaways = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await giveawaysApi.list();
      setGiveaways(res.giveaways);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load giveaways');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGiveaways(); }, [loadGiveaways]);

  // Auto-open giveaway from deep link
  useEffect(() => {
    if (!deepLinkGiveawayId) return;
    handleSelectGiveaway({ id: deepLinkGiveawayId } as Giveaway);
    onDeepLinkHandled?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkGiveawayId]);

  async function handleSelectGiveaway(g: Giveaway) {
    try {
      const detail = await giveawaysApi.get(g.id, tgId);
      setSelected(detail);
    } catch {
      setSelected(g);
    }
  }

  const active = giveaways.filter((g) => g.status === 'active' && new Date(g.ends_at) > new Date());
  const ended = giveaways.filter((g) => g.status !== 'active' || new Date(g.ends_at) <= new Date());

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">Giveaways</h2>
          <span className="text-gray-500 text-sm">{active.length} active</span>
        </div>
        <p className="text-gray-500 text-xs mt-0.5">
          Create giveaways from your Collection tab
        </p>
      </div>

      <div className="flex-1 scroll-area p-3 space-y-3">
        {loading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-surface-card animate-pulse h-28" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-10">
            <p className="text-red-400 text-sm">{error}</p>
            <button onClick={loadGiveaways} className="text-accent-purple-light text-sm mt-2">Retry</button>
          </div>
        )}

        {!loading && !error && giveaways.length === 0 && (
          <div className="text-center py-16 space-y-3">
            <p className="text-5xl">🎁</p>
            <p className="text-white font-semibold">No giveaways yet</p>
            <p className="text-gray-400 text-sm">Create one from your Collection!</p>
          </div>
        )}

        {!loading && !error && active.length > 0 && (
          <>
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide">Active</p>
            {active.map((g) => (
              <GiveawayCard key={g.id} giveaway={g} onClick={() => handleSelectGiveaway(g)} />
            ))}
          </>
        )}

        {!loading && !error && ended.length > 0 && (
          <>
            <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mt-4">Ended</p>
            {ended.map((g) => (
              <GiveawayCard key={g.id} giveaway={g} onClick={() => handleSelectGiveaway(g)} />
            ))}
          </>
        )}
      </div>

      {selected && (
        <DetailModal
          giveaway={selected}
          user={user}
          onClose={() => setSelected(null)}
          onEntered={loadGiveaways}
        />
      )}
    </div>
  );
}
